import { Injectable } from '@angular/core';

/**
 * Result of request number validation containing counts and invalid requests.
 */
export interface ValidationResult {
  totalCount: number;
  hierarchicalCount: number;
  legacyCount: number;
  invalidCount: number;
  invalidRequests: Array<{ index: number; value: string }>;
}

/**
 * Regex patterns for request number validation.
 */
export const REQUEST_NUMBER_PATTERNS = {
  /**
   * Hierarchical format: 001, 001-01, 100971-003-01
   * Pattern: 3-6 digits, optionally followed by groups of 2-3 digits separated by hyphens
   */
  hierarchical: /^\d{3,6}(-\d{2,3})*$/,
  
  /**
   * Legacy format: 1, 2, 3, 123
   * Pattern: One or more digits (no hyphens)
   */
  legacy: /^\d+$/
} as const;

export const REQUEST_NUMBER_PATTERN_DESCRIPTIONS = {
  hierarchical: 'Hierarchical format: 001, 001-01, 100971-003-01',
  legacy: 'Legacy format: 1, 2, 3, 123'
} as const;

/**
 * Service for validating request number formats.
 * Supports both hierarchical (001-01) and legacy (1, 2, 3) formats.
 */
@Injectable({ providedIn: 'root' })
export class RequestNumberValidatorService {
  private readonly hierarchicalPattern = REQUEST_NUMBER_PATTERNS.hierarchical;
  private readonly legacyPattern = REQUEST_NUMBER_PATTERNS.legacy;

  /**
   * Validates an array of requests and categorizes their request numbers.
   * 
   * @param requests - Array of request objects with requestNumber property
   * @returns ValidationResult with counts by category and list of invalid requests
   */
  validate(requests: any[]): ValidationResult {
    const result: ValidationResult = {
      totalCount: requests.length,
      hierarchicalCount: 0,
      legacyCount: 0,
      invalidCount: 0,
      invalidRequests: []
    };

    requests.forEach((req, index) => {
      if (!req.requestNumber) {
        return; // Skip requests without requestNumber
      }

      const requestNumber = String(req.requestNumber);

      if (this.hierarchicalPattern.test(requestNumber)) {
        result.hierarchicalCount++;
      } else if (this.legacyPattern.test(requestNumber)) {
        result.legacyCount++;
      } else {
        result.invalidCount++;
        result.invalidRequests.push({
          index,
          value: requestNumber
        });
      }
    });

    return result;
  }

  /**
   * Validates a single request number and returns its format type.
   * 
   * @param requestNumber - The request number to validate
   * @returns 'hierarchical' | 'legacy' | 'invalid'
   */
  validateSingle(requestNumber: string): 'hierarchical' | 'legacy' | 'invalid' {
    if (this.hierarchicalPattern.test(requestNumber)) {
      return 'hierarchical';
    } else if (this.legacyPattern.test(requestNumber)) {
      return 'legacy';
    } else {
      return 'invalid';
    }
  }

  /**
   * Checks if a request number is in hierarchical format.
   * 
   * @param requestNumber - The request number to check
   * @returns true if hierarchical, false otherwise
   */
  isHierarchical(requestNumber: string): boolean {
    return this.hierarchicalPattern.test(requestNumber);
  }

  /**
   * Checks if a request number is in legacy format.
   * 
   * @param requestNumber - The request number to check
   * @returns true if legacy, false otherwise
   */
  isLegacy(requestNumber: string): boolean {
    return this.legacyPattern.test(requestNumber);
  }
}